/**
 * Flashcard activity (swipeable card stack) — Flight School (PWA parity).
 *
 * Rebuilt to mirror the iOS source of truth
 * (client-native/src/features/flight-school/components/FlashcardActivity.tsx):
 * a behind/top card stack, a 3D rotateY flip on tap, post-flip drag-to-swipe
 * left/right with overlays + gradient action buttons, COMMON-word auto-advance,
 * and a confetti completion screen. RN StyleSheet/reanimated translated to
 * inline styles + CSS @keyframes for the same visible result.
 */
import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useImperativeHandle,
  forwardRef,
  CSSProperties,
  PointerEvent as ReactPointerEvent,
} from "react";
import { Volume2, X, Check, Book } from "lucide-react";
import { colors, FlagIcon } from "@/ui";
import Confetti from "@/ui/Confetti";
import parrotMethod from "@/features/tutorial/assets/parrot-method.png";
import { CognateWord, FlashcardData } from "../../lib/activityTypes";
import { syncFsWord } from "./_shared";
import { useWordPronunciation } from "../../hooks/useWordPronunciation";
import { useSound } from "@/hooks/useSound";

// ── iOS tuning constants (Dimensions/thresholds) ─────────────────────────────
// Web has no fixed screen width; clamp the card to a comfortable column.
const CARD_WIDTH = 320; // ~ SCREEN_WIDTH * 0.85 on a phone-sized column
const CARD_HEIGHT = Math.round(CARD_WIDTH * 1.2);
const SWIPE_THRESHOLD = 90; // px drag past which a swipe commits
const SWIPE_VELOCITY_THRESHOLD = 0.5; // px/ms (=500 px/s, iOS SWIPE_VELOCITY_THRESHOLD)
const DRAG_FEEDBACK_THRESHOLD = 50; // px drag at which button feedback turns on
const FLY_OUT = 600; // px the card flies off-screen on commit

// iOS Orange (#f06b00) — the brand orange; the design spec calls for #F06B00.
const ORANGE = colors.orange; // "#f06b00"
const ESSENTIAL_GREEN = colors.activityCorrect; // iOS #2FBF71 (not brand #34A853)

// Gradient colors for action buttons (verbatim from iOS).
const LEARNING_GRADIENT = "linear-gradient(135deg, #F45B5B 0%, #D63031 50%)";
const KNOW_IT_GRADIENT = "linear-gradient(135deg, #2ECC71 0%, #1E9B4B 55%)";

// One-time injection of the @keyframes the card animations rely on.
const KEYFRAMES_ID = "fs-flashcard-keyframes";
function ensureKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement("style");
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes fsFingerHint {
      0%   { transform: translateX(0) scale(1);    opacity: 0; }
      10%  { transform: translateX(0) scale(1);    opacity: 1; }
      20%  { transform: translateX(0) scale(0.8);  opacity: 1; }
      30%  { transform: translateX(0) scale(1);    opacity: 1; }
      65%  { transform: translateX(120px) scale(1);opacity: 1; }
      80%  { transform: translateX(120px) scale(1);opacity: 1; }
      100% { transform: translateX(120px) scale(1);opacity: 0; }
    }
    @keyframes fsCompletionPop {
      0%   { opacity: 0; transform: translateY(16px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    @keyframes fsCompletionFade {
      0%   { opacity: 0; }
      100% { opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}
// Inject at module load — it's a guarded, idempotent imperative DOM write with
// no reason to defer to a component effect.
ensureKeyframes();

// ── Animated counter (iOS AnimatedCounter) ──────────────────────────────────
function AnimatedCounter({
  target,
  duration = 1500,
}: {
  target: number;
  duration?: number;
}) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let startTime: number | null = null;
    let raf = 0;
    const animate = (ts: number) => {
      if (startTime === null) startTime = ts;
      const elapsed = ts - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setDisplayValue(Math.round(eased * target));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    const timeout = setTimeout(() => {
      raf = requestAnimationFrame(animate);
    }, 400);
    return () => {
      clearTimeout(timeout);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [target, duration]);

  return (
    <span
      style={{
        fontSize: 64,
        fontWeight: 800,
        color: colors.primary,
        letterSpacing: -1,
      }}
    >
      {displayValue}
    </span>
  );
}

// ── Swipe tutorial finger hint (iOS SwipeTutorialHint) ───────────────────────
function SwipeTutorialHint({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        zIndex: 200,
        pointerEvents: "none",
        fontSize: 48,
        // Loop the press→swipe-right gesture every 3.5s (iOS interval).
        animation: "fsFingerHint 3.5s ease-in-out 1.2s infinite",
      }}
    >
      👆
    </div>
  );
}

// ── Audio circle (front 48px, back 36px) ─────────────────────────────────────
function AudioCircle({
  size,
  playing,
  onPlay,
}: {
  size: number;
  playing: boolean;
  onPlay: (e: ReactPointerEvent | React.MouseEvent) => void;
}) {
  const iconSize = size >= 48 ? 24 : 20;
  return (
    <button
      type="button"
      aria-label="Play pronunciation"
      onPointerDown={(e) => {
        // Stop the flip/drag handlers on the card from firing.
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
        onPlay(e);
      }}
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: playing ? ORANGE : colors.lightYellow,
        flexShrink: 0,
        padding: 0,
      }}
    >
      <Volume2 size={iconSize} color={playing ? colors.white : ORANGE} />
    </button>
  );
}

// ── Word card (top = interactive, behind = static peek) ──────────────────────
interface WordCardProps {
  word: CognateWord;
  isTop: boolean;
  isCommonWord?: boolean;
  isPlayingAudio?: boolean;
  onPlayAudio?: (wordId: string) => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onAutoAdvance?: () => void;
  onDragDirectionChange?: (dir: "left" | "right" | null) => void;
  onFlipStateChange?: (flipped: boolean) => void;
}

/** Imperative handle the parent uses to drive the top card's action buttons. */
interface WordCardHandle {
  swipe: (dir: "left" | "right") => void;
  flip: () => void;
}

const cardBaseStyle: CSSProperties = {
  width: CARD_WIDTH,
  height: CARD_HEIGHT,
  background: colors.white,
  borderRadius: 24,
  boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
  position: "absolute",
};

const cardFaceStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  background: colors.white,
  borderRadius: 24,
  padding: 24,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  WebkitBackfaceVisibility: "hidden",
  backfaceVisibility: "hidden",
};

const spanishTextStyle: CSSProperties = {
  fontSize: 32,
  fontWeight: 700,
  color: ORANGE,
  textAlign: "center",
};

const cardHintStyle: CSSProperties = {
  marginTop: 24,
  fontSize: 14,
  color: colors.muted,
};

const WordCard = forwardRef<WordCardHandle, WordCardProps>(function WordCard({
  word,
  isTop,
  isCommonWord,
  isPlayingAudio,
  onPlayAudio,
  onSwipeLeft,
  onSwipeRight,
  onAutoAdvance,
  onDragDirectionChange,
  onFlipStateChange,
}: WordCardProps, ref: React.Ref<WordCardHandle>) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [isLeaving, setIsLeaving] = useState(false);
  // Synchronous mirror of `isLeaving` — two swipes/button presses in the same
  // tick would both see the React state as false (pre-re-render) and each start
  // a fly-out, firing onSwipe twice for one card. The ref guards against that.
  const isLeavingRef = useRef(false);
  const autoAdvancedRef = useRef(false);
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastDirRef = useRef<"left" | "right" | null>(null);

  // Pointer drag state.
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  // Mirrors `dragX` synchronously — `onPointerUp` must read the latest drag, not
  // the possibly-stale React state from the last `onPointerMove` render.
  const dragXRef = useRef(0);
  // Holds the 200ms fly-out timer so leaving the step mid-animation can't record
  // a swipe / advance the deck after unmount.
  const swipeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Pointer velocity tracking — iOS commits a swipe on distance OR a fast flick.
  const lastSampleRef = useRef({ x: 0, t: 0 });
  const velocityRef = useRef(0);

  // Keep latest callbacks accessible from imperative handlers.
  const flippedRef = useRef(isFlipped);
  flippedRef.current = isFlipped;

  const commitSwipe = useCallback(
    (dir: "left" | "right") => {
      if (isLeavingRef.current) return;
      isLeavingRef.current = true;
      // A manual swipe cancels any pending COMMON-word auto-advance so the card
      // can't be advanced twice (duplicate/skipped progress).
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
      setIsLeaving(true);
      const flyTo = dir === "right" ? FLY_OUT : -FLY_OUT;
      dragXRef.current = flyTo;
      setDragX(flyTo);
      // Match iOS 200ms fly-out before the parent advances.
      swipeTimerRef.current = setTimeout(() => {
        swipeTimerRef.current = null;
        if (dir === "right") onSwipeRight?.();
        else onSwipeLeft?.();
      }, 200);
    },
    [onSwipeLeft, onSwipeRight],
  );

  const handleFlip = useCallback(() => {
    if (flippedRef.current) return;
    setIsFlipped(true);
    onFlipStateChange?.(true);
    // For COMMON words, auto-advance (as "learning") ~1500ms after the flip.
    // The timer is held in a ref so a manual swipe (or unmount) can cancel it
    // before it fires — otherwise it would advance an already-advanced card.
    if (isCommonWord && onAutoAdvance && !autoAdvancedRef.current) {
      autoAdvancedRef.current = true;
      autoAdvanceTimerRef.current = setTimeout(() => {
        autoAdvanceTimerRef.current = null;
        onAutoAdvance();
      }, 1500);
    }
  }, [isCommonWord, onAutoAdvance, onFlipStateChange]);

  // Cancel pending auto-advance + fly-out timers if the card unmounts first.
  useEffect(
    () => () => {
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
      if (swipeTimerRef.current) clearTimeout(swipeTimerRef.current);
    },
    [],
  );

  // Expose imperative swipe/flip to the parent for the action buttons. Only the
  // top card is given a ref by the parent, so the behind card no-ops here.
  useImperativeHandle(
    ref,
    () => ({
      swipe: (dir: "left" | "right") => commitSwipe(dir),
      flip: () => handleFlip(),
    }),
    [commitSwipe, handleFlip],
  );

  const reportDir = useCallback(
    (dir: "left" | "right" | null) => {
      if (dir !== lastDirRef.current) {
        lastDirRef.current = dir;
        onDragDirectionChange?.(dir);
      }
    },
    [onDragDirectionChange],
  );

  // ── Drag handlers (only active once flipped) ──
  const onPointerDown = (e: ReactPointerEvent) => {
    if (!isTop) return;
    if (!flippedRef.current) {
      handleFlip();
      return;
    }
    // The learner is now classifying the card by hand — cancel the COMMON-word
    // auto-advance so it can't fire mid-gesture and advance the deck as
    // "learning" while they're dragging.
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    draggingRef.current = true;
    startXRef.current = e.clientX;
    lastSampleRef.current = { x: e.clientX, t: performance.now() };
    velocityRef.current = 0;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - startXRef.current;
    dragXRef.current = dx;
    setDragX(dx);
    // Track instantaneous velocity (px/ms) for the flick-to-commit gesture.
    const t = performance.now();
    const dt = t - lastSampleRef.current.t;
    if (dt > 0) velocityRef.current = (e.clientX - lastSampleRef.current.x) / dt;
    lastSampleRef.current = { x: e.clientX, t };
    if (dx > DRAG_FEEDBACK_THRESHOLD) reportDir("right");
    else if (dx < -DRAG_FEEDBACK_THRESHOLD) reportDir("left");
    else reportDir(null);
  };

  const onPointerUp = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    reportDir(null);
    // Read the synchronous ref, not `dragX` state (which can lag the last move).
    const dx = dragXRef.current;
    const vx = velocityRef.current;
    // iOS commits on distance OR a fast flick (SWIPE_VELOCITY_THRESHOLD), so a
    // quick short flick released before SWIPE_THRESHOLD still swipes.
    if (dx > SWIPE_THRESHOLD || vx > SWIPE_VELOCITY_THRESHOLD) {
      commitSwipe("right");
    } else if (dx < -SWIPE_THRESHOLD || vx < -SWIPE_VELOCITY_THRESHOLD) {
      commitSwipe("left");
    } else {
      dragXRef.current = 0;
      setDragX(0); // spring back
    }
  };

  // rotate up to ±15deg based on drag (iOS interpolate).
  const rotate = Math.max(-15, Math.min(15, (dragX / (CARD_WIDTH / 2)) * 15));

  // Behind card: static peek matching the front face (so the swap is seamless).
  if (!isTop) {
    return (
      <div style={{ ...cardBaseStyle, zIndex: 0 }}>
        <div
          style={{
            ...cardFaceStyle,
            position: "relative",
            inset: undefined,
            height: "100%",
            WebkitBackfaceVisibility: "visible",
            backfaceVisibility: "visible",
          }}
        >
          <FlagIcon countryCode="ES" size="large" style={{ marginBottom: 8 }} />
          <span style={spanishTextStyle}>{word.spanish}</span>
          <div
            style={{
              marginTop: 16,
              width: 48,
              height: 48,
              borderRadius: 24,
              background: colors.lightYellow,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Volume2 size={24} color={ORANGE} />
          </div>
          <span style={cardHintStyle}>Tap to reveal</span>
        </div>
      </div>
    );
  }

  const transitionDrag = draggingRef.current
    ? "none"
    : "transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)";

  return (
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        ...cardBaseStyle,
        zIndex: 1,
        cursor: isFlipped ? "grab" : "pointer",
        touchAction: "pan-y",
        transform: `translateX(${dragX}px) rotate(${rotate}deg)`,
        transition: isLeaving
          ? "transform 0.2s ease-out, opacity 0.2s ease-out"
          : transitionDrag,
        opacity: isLeaving ? 0 : 1,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          perspective: 1200,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            transformStyle: "preserve-3d",
            transition: "transform 0.3s",
            transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          <div style={cardFaceStyle}>
            <FlagIcon countryCode="ES" size="large" style={{ marginBottom: 8 }} />
            <span style={spanishTextStyle}>{word.spanish}</span>
            {onPlayAudio && (
              <div style={{ marginTop: 16 }}>
                <AudioCircle
                  size={48}
                  playing={!!isPlayingAudio}
                  onPlay={() => onPlayAudio(word.id)}
                />
              </div>
            )}
            <span style={cardHintStyle}>Tap to reveal</span>
          </div>

          <div
            style={{
              ...cardFaceStyle,
              transform: "rotateY(180deg)",
            }}
          >
            {isCommonWord && (
              <div
                style={{
                  position: "absolute",
                  top: 16,
                  background: ORANGE,
                  padding: "6px 12px",
                  borderRadius: 12,
                }}
              >
                <span
                  style={{
                    color: colors.white,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Essential Word
                </span>
              </div>
            )}
            <FlagIcon countryCode="ES" size="large" style={{ marginBottom: 8 }} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span style={spanishTextStyle}>{word.spanish}</span>
              {onPlayAudio && (
                <AudioCircle
                  size={36}
                  playing={!!isPlayingAudio}
                  onPlay={() => onPlayAudio(word.id)}
                />
              )}
            </div>
            <div
              style={{
                width: "80%",
                height: 1,
                background: colors.greyLight,
                margin: "24px 0",
              }}
            />
            <FlagIcon countryCode="UK" size="large" style={{ marginBottom: 8 }} />
            <span
              style={{
                fontSize: 28,
                fontWeight: 600,
                color: colors.primary,
                textAlign: "center",
              }}
            >
              {word.english}
            </span>
          </div>
        </div>
      </div>

      {/* Know-it overlay (drag right) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(52, 168, 83, 0.9)",
          opacity: Math.max(0, Math.min(1, dragX / SWIPE_THRESHOLD)),
          pointerEvents: "none",
        }}
      >
        <span style={{ fontSize: 24, fontWeight: 700, color: colors.white }}>
          I know this ✓
        </span>
      </div>

      {/* Learning overlay (drag left) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(240, 107, 0, 0.9)",
          opacity: Math.max(0, Math.min(1, -dragX / SWIPE_THRESHOLD)),
          pointerEvents: "none",
        }}
      >
        <span style={{ fontSize: 24, fontWeight: 700, color: colors.white }}>
          I'm learning
        </span>
      </div>
    </div>
  );
});

// ── Action button (90×90 circle) ─────────────────────────────────────────────
function ActionButton({
  variant,
  flipped,
  active,
  onPress,
}: {
  variant: "learning" | "know";
  flipped: boolean;
  active: boolean; // drag is heading this direction
  onPress: () => void;
}) {
  const isLearning = variant === "learning";
  const label = isLearning ? "I'm learning" : "I know this";

  let background: string = colors.white;
  let borderColor: string = colors.greyLight;
  let iconColor: string;
  let textColor: string = colors.muted;

  if (!flipped) {
    iconColor = colors.grey;
  } else if (active) {
    background = isLearning ? LEARNING_GRADIENT : KNOW_IT_GRADIENT;
    borderColor = "transparent";
    iconColor = colors.white;
    textColor = colors.white;
  } else {
    iconColor = isLearning ? colors.activityIncorrect : colors.activityCorrect;
  }

  return (
    <button
      type="button"
      // Not natively `disabled` before the flip — a disabled <button> swallows
      // pointer events, so tapping the bottom button strip wouldn't bubble to
      // the outer flip handler (iOS flips on a tap ANYWHERE, including here). The
      // swipe action is instead guarded to fire only once flipped.
      onClick={() => {
        if (flipped) onPress();
      }}
      aria-disabled={!flipped}
      aria-label={label}
      style={{
        width: 90,
        height: 90,
        borderRadius: 45,
        border: borderColor === "transparent" ? "none" : `1px solid ${borderColor}`,
        background,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        alignItems: "center",
        justifyContent: "center",
        cursor: flipped ? "pointer" : "default",
        opacity: flipped ? 1 : 0.5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        overflow: "hidden",
        padding: 0,
        transition: "background 0.15s ease, opacity 0.15s ease",
      }}
    >
      {isLearning ? (
        <X size={24} color={iconColor} strokeWidth={3} />
      ) : (
        <Check size={23} color={iconColor} strokeWidth={3} />
      )}
      <span style={{ fontSize: 10, fontWeight: 600, color: textColor }}>
        {label}
      </span>
    </button>
  );
}

// ── Completion screen (iOS completion view) ──────────────────────────────────
function CompletionScreen({
  total,
  knownCount,
  learningCount,
  onContinue,
}: {
  total: number;
  knownCount: number;
  learningCount: number;
  onContinue: () => void;
}) {
  return (
    <div
      style={{
        position: "relative",
        flex: 1,
        minHeight: 0,
        width: "100%",
        background: colors.background,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <Confetti active={true} />

      <div
        style={{
          marginBottom: 16,
          animation: "fsCompletionPop 0.5s ease 0.1s both",
        }}
      >
        <img
          src={parrotMethod}
          alt="Parrot"
          style={{ width: 80, height: 80, objectFit: "contain" }}
        />
      </div>

      <div style={{ animation: "fsCompletionPop 0.5s ease 0.2s both" }}>
        <h2
          style={{
            fontSize: 32,
            fontWeight: 700,
            color: colors.primary,
            textAlign: "center",
            margin: 0,
            marginBottom: 24,
          }}
        >
          Amazing Progress!
        </h2>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginBottom: 32,
          animation: "fsCompletionFade 0.5s ease 0.4s both",
        }}
      >
        <span
          style={{
            fontSize: 16,
            color: colors.muted,
            fontWeight: 500,
            marginBottom: 8,
          }}
        >
          Words reviewed
        </span>
        <AnimatedCounter target={total} />
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          gap: 20,
          animation: "fsCompletionPop 0.5s ease 0.6s both",
        }}
      >
        <StatBox
          icon={<Check size={28} color={ESSENTIAL_GREEN} strokeWidth={3} />}
          count={knownCount}
          label="Known"
          borderColor={ESSENTIAL_GREEN}
        />
        <StatBox
          icon={<Book size={28} color={ORANGE} />}
          count={learningCount}
          label="Learning"
          borderColor={ORANGE}
        />
      </div>

      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: 24,
          paddingBottom: 40,
          animation: "fsCompletionPop 0.5s ease 0.8s both",
        }}
      >
        <button
          type="button"
          onClick={onContinue}
          style={{
            width: "100%",
            padding: "16px 0",
            borderRadius: 16,
            border: "none",
            background: ORANGE,
            color: colors.white,
            fontSize: 18,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          See Your Words
        </button>
      </div>
    </div>
  );
}

function StatBox({
  icon,
  count,
  label,
  borderColor,
}: {
  icon: React.ReactNode;
  count: number;
  label: string;
  borderColor: string;
}) {
  return (
    <div
      style={{
        background: colors.white,
        padding: "20px 28px",
        borderRadius: 20,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 120,
        border: `2px solid ${borderColor}`,
        boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
      }}
    >
      {icon}
      <span
        style={{
          fontSize: 36,
          fontWeight: 700,
          color: colors.primary,
          marginTop: 8,
        }}
      >
        {count}
      </span>
      <span
        style={{
          fontSize: 14,
          color: colors.muted,
          marginTop: 4,
          fontWeight: 500,
        }}
      >
        {label}
      </span>
    </div>
  );
}

// ── Main activity ────────────────────────────────────────────────────────────
export function FlashcardActivity({
  data,
  onComplete,
}: {
  data: FlashcardData;
  onComplete: (knownWordIds?: string[], learningWordIds?: string[]) => void;
}) {
  const [index, setIndex] = useState(0);
  const knownRef = useRef<string[]>([]);
  const learningRef = useRef<string[]>([]);
  const [knownCount, setKnownCount] = useState(0);
  const [learningCount, setLearningCount] = useState(0);
  const [dragDirection, setDragDirection] = useState<"left" | "right" | null>(
    null,
  );
  const [isCardFlipped, setIsCardFlipped] = useState(false);
  const [hasSwipedOnce, setHasSwipedOnce] = useState(false);

  const { playWord, isPlaying } = useWordPronunciation();
  const { playCorrectSound } = useSound();

  const controlRef = useRef<WordCardHandle>(null);

  const word = data.words[index];
  const nextWord = data.words[index + 1];
  const isComplete = index >= data.words.length;
  const isCommon = word?.pattern === "COMMON";

  // Auto-play pronunciation when a new card appears (iOS 400ms delay).
  useEffect(() => {
    if (isComplete || !word) return;
    const timer = setTimeout(() => playWord(word.id), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const advance = useCallback(() => {
    if (!hasSwipedOnce) setHasSwipedOnce(true);
    // Reset the mirrored flip/drag state in the same handler that advances to
    // the next card (React: reset state in the event handler, not an effect).
    // The top WordCard is also keyed by index so its own internal state resets.
    setIsCardFlipped(false);
    setDragDirection(null);
    setIndex((prev) => prev + 1);
  }, [hasSwipedOnce]);

  const handleSwipeRight = useCallback(() => {
    const w = data.words[index];
    if (!w) return;
    knownRef.current.push(w.id);
    setKnownCount(knownRef.current.length);
    syncFsWord(w, "known");
    playCorrectSound();
    advance();
  }, [data.words, index, playCorrectSound, advance]);

  const handleSwipeLeft = useCallback(() => {
    const w = data.words[index];
    if (!w) return;
    learningRef.current.push(w.id);
    setLearningCount(learningRef.current.length);
    syncFsWord(w, "learning");
    advance();
  }, [data.words, index, advance]);

  const handleAutoAdvance = useCallback(() => {
    const w = data.words[index];
    if (!w || w.pattern !== "COMMON") return;
    learningRef.current.push(w.id);
    setLearningCount(learningRef.current.length);
    syncFsWord(w, "learning");
    advance();
  }, [data.words, index, advance]);

  const handleLearningPress = useCallback(() => {
    if (!isCardFlipped) return;
    controlRef.current?.swipe("left");
  }, [isCardFlipped]);

  const handleKnowItPress = useCallback(() => {
    if (!isCardFlipped) return;
    controlRef.current?.swipe("right");
  }, [isCardFlipped]);

  // ── Completion ──
  if (isComplete) {
    return (
      <CompletionScreen
        total={data.words.length}
        knownCount={knownCount}
        learningCount={learningCount}
        onContinue={() => onComplete(knownRef.current, learningRef.current)}
      />
    );
  }

  return (
    <div
      // Tap ANYWHERE reveals the definition (iOS wraps the whole screen in a
      // Pressable -> handleScreenTap). flip() is idempotent (no-op once flipped),
      // so this composes with the card's own tap + the action buttons.
      onPointerDown={() => {
        if (!isCardFlipped) controlRef.current?.flip();
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        width: "100%",
        background: colors.background,
      }}
    >
      <div style={{ textAlign: "center", padding: "12px 0" }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: colors.secondary }}>
          {index + 1} / {data.words.length}
        </span>
      </div>

      <div style={{ padding: "0 24px", marginBottom: 16 }}>
        <p
          style={{
            fontSize: 16,
            color: colors.muted,
            textAlign: "center",
            margin: 0,
          }}
        >
          Review the words you've learned
        </p>
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          minHeight: 0,
        }}
      >
        {nextWord && (
          <WordCard
            key={`behind-${nextWord.id}`}
            word={nextWord}
            isTop={false}
          />
        )}
        {word && (
          <WordCard
            key={word.id}
            word={word}
            isTop
            isCommonWord={isCommon}
            isPlayingAudio={isPlaying}
            onPlayAudio={playWord}
            onSwipeLeft={handleSwipeLeft}
            onSwipeRight={handleSwipeRight}
            onAutoAdvance={handleAutoAdvance}
            onDragDirectionChange={setDragDirection}
            onFlipStateChange={setIsCardFlipped}
            ref={controlRef}
          />
        )}

        <SwipeTutorialHint visible={!hasSwipedOnce && index === 0} />
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
          padding: "16px 32px",
        }}
      >
        <ActionButton
          variant="learning"
          flipped={isCardFlipped}
          active={dragDirection === "left"}
          onPress={handleLearningPress}
        />
        <ActionButton
          variant="know"
          flipped={isCardFlipped}
          active={dragDirection === "right"}
          onPress={handleKnowItPress}
        />
      </div>

      <div style={{ textAlign: "center", paddingBottom: 16 }}>
        <span style={{ fontSize: 13, color: colors.muted }}>
          {!isCardFlipped
            ? "Tap anywhere to reveal definition"
            : "Swipe right if you know it, left if you don't"}
        </span>
      </div>
    </div>
  );
}
